<!--
 * @Description: 
 * @Author: luckymiaow
 * @Date: 2023-03-08 17:51:39
 * @LastEditors: 景 彡
-->
<script setup lang="ts">
import { onMounted, ref, shallowRef, triggerRef, watch, watchEffect } from 'vue';
import { Button, Col, Row } from 'ant-design-vue';
import { DeleteOutlined, DownOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons-vue';
import type { ColProps, RowProps } from 'ant-design-vue';
import { deepCopy } from '../../util';
import { Form, FormItem } from '../../form/';
import type { FieldKeys, SearchField } from '../types';
import SearchFormItem from '../../pro-form/src/_FormItem.vue';

const props = withDefaults(
  defineProps<{
    fields: SearchField[];
    value?: { [key: string]: any };
    row?: RowProps;
    col?: ColProps & { span: string | number | undefined };
    showCollapse?: boolean;
    collapsed?: boolean;
    labelAlign?: 'left' | 'right';
    labelWrap?: boolean;
    labelCol?: RowProps;
    /* 显示的字段 */
    showFields?: FieldKeys;
    /* 隐藏的字段, 优先级高。与showFields互斥使用，优先使用hideFields */
    hideFields?: FieldKeys;
  }>(),
  {
    fields: () => [],
    row: () => ({
      gutter: 10,
    }),
    value: () => ({}),
    showCollapse: true,
    collapsed: false,
    labelAlign: 'right',
    labelWrap: false,
    col: () => ({ xs: 24, sm: 12, md: 12, lg: 12, xl: 8, xxl: 6, xxxl: 6, span: 6 }),
  }
);

const emits = defineEmits<{
  (event: 'update:value', form: { [key: string]: any }): void;
  (event: 'search', form: { [key: string]: any }): void;
  (event: 'reset', form: { [key: string]: any }): void;
}>();

/* 表单 */
const _value = ref(deepCopy(props.value));

onMounted(() => {
  _value.value = {
    ..._value.value,
    ...props.fields.reduce((a, b) => {
      if (b.prop) a[b.prop] = props.value[b.prop] ?? b.emptyValue ?? null;
      return a;
    }, {} as any),
  };
  emits('update:value', _value.value);
});

watch(
  _value,
  val => {
    emits('update:value', val);
  },
  { deep: true }
);

/* 实体 */
const _fields = shallowRef<SearchField[]>([]);

const _collapsed = ref(false);

watchEffect(() => {
  _collapsed.value = props.collapsed;
});

const _showCollapse = ref(props.showCollapse);

watchEffect(() => {
  _showCollapse.value = props.showCollapse;
});

/* 计算实际占用的范围 */
const colRange = (col: ColProps): number => {
  const width = document.body.clientWidth;
  const size: any = {
    xxxl: () => width >= 2000,
    xxl: () => width >= 1600,
    xl: () => width >= 1200,
    lg: () => width >= 992,
    md: () => width >= 768,
    sm: () => width >= 576,
    xs: () => width < 576,
  };
  let res: keyof ColProps = 'span';

  for (const k in size) {
    if (size[k]()) {
      res = k as keyof ColProps;
      break;
    }
  }
  return Number(res in col ? col[res] : col?.span || 0) + Number(col.offset || 0);
};

let _sortData: SearchField[] = [];

/* 初始化col属性 */
const colBindInit = () => {
  let sum = 0;
  _showCollapse.value = false;
  _sortData.forEach((item, index) => {
    const _item = deepCopy(item);
    if (!_item?.col) {
      Object.assign(_item, { col: { ...props.col } });
    } else if (!_item.col.span) {
      Object.assign(_item.col, { ...props.col });
    }
    if (_item.col) {
      sum += colRange(_item.col);
      if (sum > 18 && index !== 0) {
        if (props.showCollapse === true) _showCollapse.value = true;
        if (_collapsed.value === true)
          Object.assign(_item.col, { xs: 0, sm: 0, md: 0, lg: 0, xl: 0, xxl: 0, xxxl: 0, span: 0 });
      }
    }
    _fields.value[index] = _item;
  });
  triggerRef(_fields);
};

watch(
  () => props.fields,
  val => {
    _sortData = deepCopy(val).sort((a, b) => {
      const orderA = a.order ?? Number.MIN_VALUE;
      const orderB = b.order ?? Number.MIN_VALUE;
      if (orderA > orderB) {
        return -1;
      } else if (orderA < orderB) {
        return 1;
      } else {
        return 0;
      }
    });
    _fields.value = deepCopy(_sortData);
    colBindInit();
  },
  { immediate: true }
);

const collapseChange = () => {
  _collapsed.value = !_collapsed.value;
  colBindInit();
};

function isShow(item: SearchField): boolean {
  const field = item.prop;
  if (!field) return true;
  if (props.hideFields) return !props.hideFields.includes(field);
  if (props.showFields) return props.showFields.includes(field);
  return true;
}

const search = () => {
  emits('search', _value.value);
};
const reset = () => {
  const map = _fields.value.reduce<Record<string, SearchField>>((a, b) => (b.prop ? (a[b.prop] = b) : '', a), {});

  Object.keys(_value.value).forEach(key => {
    _value.value[key] = map[key]?.emptyValue;
  });
  emits('reset', _value.value);
};
</script>

<script lang="ts">
export default {
  name: 'SearchForm',
};
</script>

<template>
  <div v-if="fields.length">
    <Form
      ref="formRef"
      :model="_value"
      :labelAlign="labelAlign"
      :labelWrap="labelWrap"
      :labelCol="labelCol"
      @finish="search"
    >
      <Row v-bind="row">
        <Col v-for="(item, index) in _fields" v-show="isShow(item)" :key="item.prop" v-bind="item.col" :index="index">
          <FormItem
            v-bind="typeof item.label === 'object' ? {} : { label: item.label }"
            :labelAlign="item.labelAlign"
            :labelCol="item.labelCol"
          >
            <template #label>
              <component :is="item.label" />
            </template>
            <SearchFormItem :model="_value" :field="item" />
          </FormItem>
        </Col>
        <Col class="suffix">
          <div class="operation">
            <Button type="primary" @click="search"><search-outlined />搜索</Button>
            <Button @click="reset"><delete-outlined />重置</Button>
            <Button v-if="_showCollapse" type="link" class="search-isOpen" @click="collapseChange()">
              {{ _collapsed ? '展开' : '合并' }}
              <component :is="_collapsed ? DownOutlined : UpOutlined"></component>
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  </div>
</template>

<style lang="less" scoped>
.suffix {
  margin-left: auto;
  margin-bottom: 24px;
  padding: 0 !important;
  .operation {
    display: flex;
    gap: 10px;
    .search-isOpen {
      padding: 0;
    }
  }
}
</style>
